import { Suspense } from 'react';

import { Navigate } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useDefaultSportPages } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { SportSwitcher } from '@sorare/core/src/contexts/sport/SportSwitcher';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { lazy } from '@sorare/core/src/lib/retry';
import { sportFromCountry } from '@sorare/core/src/lib/routing/sportFromPath';

import { AppLayout } from '@sorare/gameplay/src/components/navigation/AppLayout';

const NBALanding = lazy(async () => import('@sorare/nba/src/pages/page'));
const FootballLanding = lazy(
  async () => import('@sorare/football/src/pages/page')
);
const MLBLanding = lazy(async () => import('@sorare/baseball/src/pages/page'));

const landings = {
  [Sport.BASEBALL]: MLBLanding,
  [Sport.NBA]: NBALanding,
  [Sport.FOOTBALL]: FootballLanding,
};

const Landing = () => {
  const {
    flags: { landingRedirection },
  } = useFeatureFlags();
  const { sport } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const defaultSportPages = useDefaultSportPages();

  if (isLoggedIn) {
    return (
      <Navigate to={`${defaultSportPages[sport]}`} replace keepSearchParams />
    );
  }

  /** Don't use the sport context here because it changes when we navigate to a sport page like /nba */
  const chosenSport = sportFromCountry(landingRedirection as Sport, true);
  const SportLanding = landings[chosenSport];

  return (
    <Suspense>
      <AppLayout>
        <SportLanding isRoot />
        <SportSwitcher sport={chosenSport} />
      </AppLayout>
    </Suspense>
  );
};

export default Landing;
